// components/UI/Table/hooks/useTable.ts
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { filterAtom } from "components/UI/Table/atoms/filterAtom";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { timeFilterAtom } from "components/UI/Table/atoms/timeFilterAtom";
import { DEFAULT_TIME_FILTER_PARAM, FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableLocalStorage } from "components/UI/Table/hooks/useTableLocalStorage.ts";
import { useTableUrlParameters } from "components/UI/Table/hooks/useTableUrlParameters.ts";
import { FilterRule, FilterType } from "components/UI/Table/types/filter";
import {
    QuickFilterOption,
    TableColumnType,
    TableLocalStorageConfig,
    TableState,
} from "components/UI/Table/types/table.ts";
import { getSortOrder } from "components/UI/Table/utils/getSortInfo";
import { normalizeValuelessFilterOperators } from "components/UI/Table/utils/normalizeValuelessFilterOperators";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { SearchFilterEnum } from "types/enums";
import { Identifiable, TableId } from "types/models.ts";

import PageRequest = App.Http.Requests.PageRequest;

type DefaultSortObj = { param: string; key: string; order: "ascend" | "descend" } | undefined;

const resolveDefaultSort = <T extends Identifiable>(cols?: TableColumnType<T>[]): DefaultSortObj => {
    if (!cols) return undefined;
    const col = cols.find((c) => c.defaultSorter);
    if (!col) return undefined;

    const key = (
        col.sortName ??
        col.filterName ??
        (typeof col.dataIndex === "string" ? col.dataIndex : "")
    ) as string;
    const order = col.defaultSortOrder ?? "ascend";
    const param = order === "descend" ? `-${key}` : key;

    return { param, key, order };
};

export const useTable = <T extends Identifiable>(
    tableId: TableId,
    timeFilterParam: string = DEFAULT_TIME_FILTER_PARAM
) => {
    const tableLocalStorageUtils = useTableLocalStorage<T>(tableId);
    const tableUrlParametersUtils = useTableUrlParameters<T>(tableId);
    const tableAtomInstance = tableAtom<T>();
    const [tableState, setTableState] = useRecoilState(tableAtomInstance(tableId));
    const [filters, setFilters] = useRecoilState(filterAtom(tableId));
    const [timeFilter, setTimeFilter] = useRecoilState(timeFilterAtom);
    const [, setExportState] = useRecoilState(exportAtom(tableId));

    const DEFAULT_SORT_OBJ = resolveDefaultSort(tableState.originColumns || []);
    const DEFAULT_SORT = DEFAULT_SORT_OBJ?.param;

    useEffect(() => {
        fetchColumns();
    }, [tableState.config, tableState.editingRowId, tableState.filter?.sort]);

    const fetchColumns = () => {
        if (!tableState.config || !tableState.originColumns) return;

        const columnsConfig = tableState.config.columnsConfig;
        const columns = tableState.originColumns;

        const activeColumns = columnsConfig.filter((column) => column.visible);
        const sortedColumns = activeColumns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const mappedColumns = sortedColumns
            .map((config) => columns.find((column) => column.dataIndex === config.dataIndex))
            .filter((column): column is TableColumnType<T> => Boolean(column));

        const currentSort = tableState?.filter?.sort ?? DEFAULT_SORT;

        const draggableColumns = mappedColumns.map((column) => {
            const baseColumn = {
                ...column,
                sortOrder: getSortOrder(column?.dataIndex as string, currentSort, column?.filterName, column?.sortName),
                onHeaderCell: () => ({ id: `${column?.key}` }),
                onCell: () => ({ id: `${column?.key}` }),
                sortDirections:
                    column.defaultSortOrder === "descend" ? ["descend", "ascend", null] : ["ascend", "descend", null],
            };

            if (!column?.editable) return baseColumn;

            return {
                ...baseColumn,
                onCell: (record: T) => ({
                    ...record, // TODO
                    editable: column.editable, // TODO
                    dataIndex: column.dataIndex, // TODO
                    // @ts-expect-error todo
                    title: column.title.props ? column.title.props.title : column.title, // TODO
                    handleSave: null, // TODO
                    editingRowId: tableState.editingRowId, // TODO
                    inputType: column.inputType, // TODO
                }),
            };
        });

        const headers = draggableColumns.map((col) =>
            typeof col.dataIndex === "string"
                ? col.dataIndex
                : Array.isArray(col.dataIndex)
                  ? col.dataIndex.join(".")
                  : String(col.dataIndex)
        );

        setExportState((prev) => ({
            ...prev,
            headers: headers.join(","),
        }));

        setTableState((prev) => ({ ...prev, columns: draggableColumns }) as TableState<T>);
    };

    const saveConfig = (config: TableLocalStorageConfig<T>): void => {
        tableLocalStorageUtils.setLocalStorageConfig({
            ...tableLocalStorageUtils.getLocalStorageConfig(),
            ...config,
        });
        setTableState((prev) => ({ ...prev, config }));
    };

    const savePage = (page: PageRequest): void => {
        tableUrlParametersUtils.setUrlParameters({
            ...tableUrlParametersUtils.getUrlParameters(),
            page: page,
        });
        setTableState((prev) => ({
            ...prev,
            page: { size: page.size, number: page.number },
        }));
    };

    const initializeFiltersFromUrl = () => {
        const params = tableUrlParametersUtils.getUrlParameters();
        let block = params?.filter?.filter;
        if (!block) return;

        const manualCreatedAtAllowed = tableState.originColumns?.some((c) => {
            const filterKey = c.filterName ?? c.dataIndex;
            return filterKey === timeFilterParam && c.filterable === true;
        });

        // Drop a time filter leaked into the URL from another table — this table neither
        // renders the TimeFilter nor allows the param as a manual filter column
        if (!tableState.timeFilterEnabled && !manualCreatedAtAllowed && block[timeFilterParam]) {
            block = Object.fromEntries(Object.entries(block).filter(([key]) => key !== timeFilterParam));
        }

        if (!manualCreatedAtAllowed && tableState.timeFilterEnabled) {
            const timeObj = block[timeFilterParam];
            if (timeObj) {
                const op = Object.keys(timeObj)[0] as SearchFilterEnum;
                const val = timeObj[op];
                setTimeFilter({
                    id: Date.now(),
                    dataIndex: timeFilterParam,
                    filterName: timeFilterParam,
                    filterType: FILTER_TYPES.date,
                    operator: op,
                    value: val,
                    filterOptions: [],
                });
            }
        }

        const manualRules: FilterRule[] = [];
        let idCounter = 0;

        Object.entries(block)
            .filter(([key]) => {
                if (key === timeFilterParam) return manualCreatedAtAllowed;
                return true;
            })
            .forEach(([filterKey, filterObj]) => {
                const col =
                    tableState.originColumns?.find((c) => c.filterName === filterKey || c.dataIndex === filterKey) ??
                    null;

                // Handle multiple operators for the same filter key
                Object.entries(filterObj as Record<string, unknown>).forEach(([operator, value]) => {
                    const stringValue = typeof value === "string" ? value : String(value ?? "");

                    // Split comma-separated values into separate filter rules
                    // but only for string-type filters (not dates, multiselects, in/not_in operators, etc.)
                const baseFilterType = (col?.filterType as FilterType) ?? FILTER_TYPES.default;
                const filterType =
                    col?.filterIncludeNull && !baseFilterType.operators.includes(SearchFilterEnum.Null)
                        ? { ...baseFilterType, operators: [SearchFilterEnum.Null, ...baseFilterType.operators] }
                        : baseFilterType;
                    const isMultiOperator = operator === SearchFilterEnum.In || operator === SearchFilterEnum.NotIn;
                    const shouldSplit =
                        !isMultiOperator &&
                        (filterType.type === "string" || filterType.type === "number" || filterType.type === "select");

                    if (shouldSplit && stringValue.includes(",")) {
                        const values = stringValue.split(",");
                        values.forEach((val) => {
                            manualRules.push({
                                id: Date.now() + idCounter++,
                                dataIndex: col?.dataIndex ?? filterKey,
                                filterName: col?.filterName,
                                filterType,
                                operator: operator as SearchFilterEnum,
                                value: filterType.type === "number" && !isNaN(Number(val)) ? Number(val) : val,
                                filterOptions: col?.filterOptions ?? [],
                                filterIncludeNull: col?.filterIncludeNull,
                                filterNullOptionLabel: col?.filterNullOptionLabel,
                            });
                        });
                    } else {
                        manualRules.push({
                            id: Date.now() + idCounter++,
                            dataIndex: col?.dataIndex ?? filterKey,
                            filterName: col?.filterName,
                            filterType,
                            operator: operator as SearchFilterEnum,
                            value,
                            filterOptions: col?.filterOptions ?? [],
                            filterIncludeNull: col?.filterIncludeNull,
                            filterNullOptionLabel: col?.filterNullOptionLabel,
                        });
                    }
                });
            });

        setFilters(manualRules);

        setTableState((prev) => ({
            ...prev,
            filter: { ...prev.filter, filter: block },
        }));
    };

    const saveFilters = () => {
        // Group filters by filterName/dataIndex and operator, joining values with commas
        const filterMap = filters.reduce<Record<string, Record<string, unknown>>>((acc, curr) => {
            const key = (curr.filterName || curr.dataIndex) as string;
            const existingFilter = acc[key];

            if (existingFilter && existingFilter[curr.operator] !== undefined) {
                // Same key and operator - join values with comma
                const existingValue = existingFilter[curr.operator];
                const newValue = curr.value;
                // Only join primitive values; for complex types, use the latest value
                if (typeof existingValue === "string" || typeof existingValue === "number") {
                    existingFilter[curr.operator] = `${existingValue},${newValue}`;
                } else {
                    existingFilter[curr.operator] = newValue;
                }
            } else if (existingFilter) {
                // Same key but different operator - add the new operator
                existingFilter[curr.operator] = curr.value;
            } else {
                // New key
                acc[key] = { [curr.operator]: curr.value };
            }

            return acc;
        }, {});

        const hasManual = filters.some((f) => (f.filterName ?? f.dataIndex) === timeFilterParam);
        if (timeFilter && !hasManual && tableState.timeFilterEnabled) {
            filterMap[timeFilter.dataIndex as string] = { [timeFilter.operator]: timeFilter.value };
        }

        const requestFilterMap = normalizeValuelessFilterOperators(filterMap);

        const params = tableUrlParametersUtils.getUrlParameters() || {};
        const { sort: filterSort } = params.filter || {};
        const sort = params.sort || filterSort;

        const urlFilter = { ...(params.filter || {}) };
        delete urlFilter.sort;

        tableUrlParametersUtils.setUrlParameters({
            ...params,
            page: { ...params.page, number: 1 },
            filter: { ...urlFilter, filter: requestFilterMap },
            ...(sort !== undefined && { sort }),
        });

        setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: { ...(params.filter || {}), filter: requestFilterMap, ...(sort !== undefined && { sort }) },
        }));
    };

    // `resetPage: false` is for callers that are just re-syncing state to match a value that's
    // already applied (e.g. TimeFilter's mount/table-switch effect re-establishing the current
    // URL's own filter value into state) — NOT a real user-driven filter change. Defaulting this
    // to `true` (reset page to 1, matching "changing a filter should return to page 1") broke
    // manually editing page.number in the URL: any table that renders TimeFilter re-applies its
    // date filter on every mount, which forced the page back to 1 even when the URL's page.number
    // had just been correctly read as something else by setDefaultState moments earlier.
    const saveTimeFilter = (newRule: FilterRule, options?: { resetPage?: boolean }) => {
        const resetPage = options?.resetPage ?? true;
        const params = tableUrlParametersUtils.getUrlParameters() || {};
        const existing = params.filter?.filter || {};
        const existingTimeRule = existing[timeFilterParam];
        const existingTimeValue = existingTimeRule?.[newRule.operator];
        const syncTimeFilterState = () => {
            const currentValue = tableState.filter?.filter?.[timeFilterParam]?.[newRule.operator];
            if (currentValue === newRule.value) return;

            setTableState((prev) => ({
                ...prev,
                filter: {
                    ...(prev.filter || {}),
                    filter: {
                        ...(prev.filter?.filter || {}),
                        [timeFilterParam]: { [newRule.operator]: newRule.value },
                    },
                },
            }));
        };

        // No-op if nothing actually changed — callers like TimeFilter's mount/table-switch effect
        // re-apply the same rule on every render, and without this guard each call creates a new
        // `tableState.filter` object reference, which re-triggers the row-fetch effect in
        // useTableDataApiOperations (it depends on that reference, not its value) — causing
        // redundant duplicate API calls on a single page load.
        if (
            timeFilter &&
            timeFilter.dataIndex === newRule.dataIndex &&
            timeFilter.operator === newRule.operator &&
            timeFilter.value === newRule.value &&
            existingTimeValue === newRule.value
        ) {
            syncTimeFilterState();
            return;
        }

        // The URL already contains this rule when a table is synchronizing its initial state.
        // Avoid rewriting table state and triggering duplicate API requests in that case.
        if (existingTimeValue === newRule.value) {
            syncTimeFilterState();
            setTimeFilter(newRule);
            return;
        }

        const nonTimePart = Object.fromEntries(Object.entries(existing).filter(([k]) => k !== timeFilterParam));

        const combined = {
            ...nonTimePart,
            [newRule.dataIndex as string]: { [newRule.operator]: newRule.value },
        };

        const { sort: filterSort } = params.filter || {};
        const sort = params.sort || filterSort;

        const urlFilter = { ...(params.filter || {}) };
        delete urlFilter.sort;

        const page = resetPage ? { ...params.page, number: 1 } : params.page;

        tableUrlParametersUtils.setUrlParameters({
            ...params,
            page,
            filter: { ...urlFilter, filter: combined },
            ...(sort !== undefined && { sort }),
        });

        setTableState((prev) => ({
            ...prev,
            page: resetPage ? { ...prev.page, number: 1 } : prev.page,
            filter: { ...(params.filter || {}), filter: combined, ...(sort !== undefined && { sort }) },
        }));

        setTimeFilter(newRule);
    };

    const saveSearch = (dataIndex: string, operator: SearchFilterEnum, value: string | undefined): void => {
        const params = tableUrlParametersUtils.getUrlParameters() || {};
        const existing = params.filter?.filter || {};
        const withoutSearch = Object.fromEntries(Object.entries(existing).filter(([key]) => key !== dataIndex));

        const combined = value ? { ...withoutSearch, [dataIndex]: { [operator]: value } } : withoutSearch;

        const { sort: filterSort } = params.filter || {};
        const sort = params.sort || filterSort;

        const urlFilter = { ...(params.filter || {}) };
        delete urlFilter.sort;

        tableUrlParametersUtils.setUrlParameters({
            ...params,
            page: { ...params.page, number: 1 },
            filter: { ...urlFilter, filter: combined },
            ...(sort !== undefined && { sort }),
        });

        setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: { ...(params.filter || {}), filter: combined, ...(sort !== undefined && { sort }) },
        }));

        // Keep the per-column Filter dropdown in sync, so it reflects the quick-search's
        // real operator/value instead of the blank default row it shows when filterAtom is empty.
        const col = tableState.originColumns?.find((c) => c.filterName === dataIndex || c.dataIndex === dataIndex);
        const filtersWithoutSearch = filters.filter((f) => (f.filterName ?? f.dataIndex) !== dataIndex);
        const baseFilterType = (col?.filterType as FilterType) ?? FILTER_TYPES.string;
        const filterType =
            col?.filterIncludeNull && !baseFilterType.operators.includes(SearchFilterEnum.Null)
                ? { ...baseFilterType, operators: [SearchFilterEnum.Null, ...baseFilterType.operators] }
                : baseFilterType;

        setFilters(
            value
                ? [
                      ...filtersWithoutSearch,
                      {
                          id: Date.now(),
                          dataIndex: col?.dataIndex ?? dataIndex,
                          filterName: col?.filterName,
                          filterType,
                          filterIncludeNull: col?.filterIncludeNull,
                          filterNullOptionLabel: col?.filterNullOptionLabel,
                          operator,
                          value,
                          filterOptions: col?.filterOptions ?? [],
                      },
                  ]
                : filtersWithoutSearch
        );
    };

    const saveQuickFilter = (
        dataIndex: string,
        operator: SearchFilterEnum,
        value: string | undefined,
        options: QuickFilterOption[]
    ): void => {
        saveSearch(dataIndex, operator, value);

        if (value) {
            setFilters((currentFilters) =>
                currentFilters.map((filter) =>
                    (filter.filterName ?? filter.dataIndex) === dataIndex
                        ? { ...filter, filterOptions: options }
                        : filter
                )
            );
        }
    };

    const saveSort = (incomingSort: string | undefined, userReset = false): void => {
        setTableState((prev) => {
            const newSortState = userReset ? undefined : incomingSort;
            const sortForUrl = newSortState && newSortState !== DEFAULT_SORT ? newSortState : undefined;

            const params = tableUrlParametersUtils.getUrlParameters() || {};

            const { filter: rawFilter, ...rest } = params as Record<string, any>;
            delete rest.sort;

            tableUrlParametersUtils.setUrlParameters({
                ...rest,
                ...(rawFilter && Object.keys(rawFilter).length ? { filter: rawFilter } : {}),
                ...(sortForUrl ? { sort: sortForUrl } : {}),
            });

            return {
                ...prev,
                filter: {
                    ...(prev.filter || {}),
                    sort: newSortState,
                },
            };
        });
    };

    const removeFilters = (): void => {
        const currentParams = tableUrlParametersUtils.getUrlParameters();
        if (!currentParams?.filter?.filter && !timeFilter) return;

        const preservedTimeFilter =
            timeFilter && tableState.timeFilterEnabled
                ? { [timeFilter.dataIndex as string]: { [timeFilter.operator]: timeFilter.value } }
                : {};

        const { include, sort: filterSort, fields } = currentParams?.filter || {};
        const updatedFilterBlock = {
            ...(include !== undefined && { include }),
            ...(fields !== undefined && { fields }),
            ...(Object.keys(preservedTimeFilter).length && { filter: preservedTimeFilter }),
        };

        const sort = currentParams?.sort || filterSort;

        tableUrlParametersUtils.setUrlParameters({
            ...currentParams,
            page: { ...currentParams?.page, number: 1 },
            filter: Object.keys(updatedFilterBlock).length ? updatedFilterBlock : undefined,
            ...(sort !== undefined && { sort }),
        });

        setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: { ...(prev.filter || {}), filter: preservedTimeFilter, ...(sort !== undefined && { sort }) },
        }));

        setFilters([]);
    };

    const setDefaultState = (columnConfig: TableColumnType<T>[], timeFilterEnabled = false): void => {
        const withStorage = tableLocalStorageUtils.getTableStateWithConfig(tableState, columnConfig);
        const withUrl = tableUrlParametersUtils.getTableStateWithUrlParameters(withStorage);

        const needDefaultSort =
            !withUrl.filter?.sort && DEFAULT_SORT && !tableUrlParametersUtils.getUrlParameters()?.sort;

        const finalState = needDefaultSort
            ? {
                  ...withUrl,
                  filter: { ...(withUrl.filter || {}), sort: DEFAULT_SORT },
              }
            : withUrl;

        setTableState({
            ...finalState,
            timeFilterEnabled,
            initialized: true,
        });
    };

    const setEditingRowId = (record: T) => {
        setTableState({ ...tableState, editingRowId: record.id?.toString() || "" });
    };

    const setNullEditingRowId = () => {
        setTableState({ ...tableState, editingRowId: null });
    };

    return {
        setDefaultState,
        fetchColumns,
        saveConfig,
        savePage,
        initializeFiltersFromUrl,
        saveTimeFilter,
        saveFilters,
        saveSearch,
        saveQuickFilter,
        saveSort,
        removeFilters,
        setEditingRowId,
        setNullEditingRowId,
        defaultSort: DEFAULT_SORT,
    };
};
